import React, { useRef, forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    alpha,
    Stack,
    Chip,
    Skeleton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import TapCardInput from './TapCardInput';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import CreditCardIcon from '@mui/icons-material/CreditCard';

// =============================================================================
// SUPPORTED CARD BRANDS
// =============================================================================
// const SUPPORTED_BRANDS = [
//     { name: 'Visa', color: '#1A1F71' },
//     { name: 'Mastercard', color: '#EB001B' },
//     { name: 'Mada', color: '#00A651' },
//     { name: 'Amex', color: '#006FCF' },
// ];

// =============================================================================
// TAP CREDIT CARD COMPONENT
// =============================================================================
const TapCreditCard = forwardRef(({
    amount,
    currency = 'SAR',
    customerEmail,
    customerPhone,
    customerName,
    onTokenSuccess,
    onTokenError,
    onValidityChange,
    showSaveCard = true,
    saveCardDefaultChecked = true,
    disabled = false,
}, ref) => {
    const { t } = useTranslation();
    const cardInputRef = useRef(null);
    const [isCardValid, setIsCardValid] = useState(false);

    // =============================================================================
    // EXPOSE METHODS VIA REF
    // =============================================================================
    useImperativeHandle(ref, () => ({
        tokenize: async () => {
            return cardInputRef.current?.tokenize();
        },
        reset: () => {
            cardInputRef.current?.reset();
            setIsCardValid(false);
        },
        isValid: () => isCardValid,
        isReady: () => cardInputRef.current?.isReady(),
        getSaveCard: () => cardInputRef.current?.getSaveCard(),
    }));


    return (
        <Box>
            {/* Card Form Container */}
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                }}
            >

                {/* Header */}
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <CreditCardIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                        <Typography variant="subtitle1" fontWeight={600}>
                            {t('Enter Card Details')}
                        </Typography>
                    </Stack>
                    {/* <Stack direction="row" spacing={0.5}>
                        {SUPPORTED_BRANDS.map((brand) => (
                            <Chip
                                key={brand.name}
                                label={brand.name}
                                size="small"
                                sx={{
                                    height: 20,
                                    fontSize: '0.6rem',
                                    fontWeight: 600,
                                    bgcolor: (theme) => alpha(brand.color, 0.1),
                                    color: brand.color,
                                    '& .MuiChip-label': { px: 0.75 },
                                }}
                            />
                        ))}
                    </Stack> */}
                </Stack>

                {/* Tap Card Input */}
                <Box sx={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
                    <TapCardInput
                        ref={cardInputRef}
                        amount={amount}
                        currency={currency}
                        customerEmail={customerEmail}
                        customerPhone={customerPhone}
                        customerName={customerName}
                        onSuccess={onTokenSuccess}
                        onError={onTokenError}
                        onValidInput={(value) => {
                            onValidityChange(value);
                        }}
                        // onInvalidInput={() => { }}
                        showSaveCard={showSaveCard}
                        saveCardDefaultChecked={saveCardDefaultChecked}
                        showError={true}
                    />
                </Box>
            </Paper>

            {/* Security Badge */}
            {/* <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{ mt: 2 }}
            >
                <LockOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary">
                    {t('Your payment is secured by')}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <SecurityIcon sx={{ fontSize: 14, color: 'success.main' }} />
                    <Typography variant="caption" fontWeight={600} color="success.main">
                        Tap Payments
                    </Typography>
                </Stack>
            </Stack> */}
        </Box>
    );
});

TapCreditCard.displayName = 'TapCreditCard';

export default TapCreditCard;
